import type { FunctionalComponent } from 'preact';
import { useEffect, useState } from 'preact/hooks';
import type { EventOccurrenceEntry } from '../lib/eventOccurrenceDates';
import { beginModalBodyScrollLock } from '../utils/modalScrollLock';

/** Theming: prefer CSS vars from widget (light), fallbacks for dark lounge */
const surface = 'var(--mw-surface, #131314)';
const textMain = 'var(--mw-text, #fff)';
const textMuted = 'var(--mw-text-muted, rgba(255,255,255,0.55))';
const accentKicker = 'color-mix(in srgb, var(--mw-primary, #ec13a4) 75%, #7c3aed)';

type Props = {
  open: boolean;
  onClose: () => void;
  occurrences: EventOccurrenceEntry[];
  selectedIso: string;
  onConfirm: (entry: EventOccurrenceEntry) => void;
  timezone?: string;
};

function partsForCard(iso: string, timeZone?: string) {
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return null;
    const weekday = d.toLocaleDateString('en-US', { weekday: 'short', timeZone: timeZone || undefined });
    const dayNum = d.toLocaleDateString('en-US', { day: 'numeric', timeZone: timeZone || undefined });
    const month = d.toLocaleDateString('en-US', { month: 'short', timeZone: timeZone || undefined });
    const year = d.toLocaleDateString('en-US', { year: 'numeric', timeZone: timeZone || undefined });
    return {
      weekday: weekday.toUpperCase(),
      dayNum,
      month: month.toUpperCase(),
      year,
    };
  } catch {
    return null;
  }
}

export const EventDateSelectModalWidget: FunctionalComponent<Props> = ({
  open,
  onClose,
  occurrences,
  selectedIso,
  onConfirm,
  timezone,
}) => {
  const [pending, setPending] = useState(selectedIso);

  useEffect(() => {
    if (open) setPending(selectedIso);
  }, [open, selectedIso]);

  useEffect(() => {
    if (!open) return;
    return beginModalBodyScrollLock();
  }, [open]);

  if (!open) return null;

  return (
    <div class="mw-tl-date-backdrop" onClick={onClose}>
      <div class="mw-tl-date-panel" onClick={(e) => e.stopPropagation()} role="dialog" aria-label="Select date">
        <div class="mw-tl-date-top">
          <button type="button" class="mw-tl-date-close" onClick={onClose} aria-label="Close">✕</button>
          <span class="mw-tl-date-top-title" style={{ color: 'var(--mw-primary, #ec13a4)' }}>Select date</span>
          <span class="mw-tl-date-spacer" />
        </div>
        <div class="mw-tl-date-content">
          <p class="mw-tl-date-kicker" style={{ color: accentKicker }}>Availability</p>
          <h2 class="mw-tl-date-hero" style={{ color: textMain }}>
            Pick your
            <br />
            <span style={{ color: 'var(--mw-primary, #ec13a4)' }}>moment</span>
          </h2>
          <div class="mw-tl-date-accent-bar" style={{ background: 'var(--mw-primary, #ec13a4)' }} />
          <div class="mw-tl-date-list">
            {occurrences.map((entry) => {
              const p = partsForCard(entry.iso, timezone);
              if (!p) return null;
              const isSel = pending === entry.iso;
              return (
                <button
                  type="button"
                  key={`${entry.iso}-${entry.targetEventId ?? ''}`}
                  class={isSel ? 'mw-tl-date-card mw-tl-date-card--sel' : 'mw-tl-date-card'}
                  style={isSel
                    ? {
                        background:
                          'linear-gradient(135deg, var(--mw-primary, #ec13a4) 0%, color-mix(in srgb, var(--mw-primary, #ec13a4) 55%, #000) 100%)',
                        boxShadow: '0 0 24px color-mix(in srgb, var(--mw-primary, #ec13a4) 35%, transparent)',
                      }
                    : {
                        background: surface,
                        border: '1px solid var(--mw-border, rgba(255,255,255,0.05))',
                      }
                  }
                  onClick={() => setPending(entry.iso)}
                >
                  <div>
                    <div
                      class="mw-tl-date-wd"
                      style={{ color: isSel ? 'var(--mw-primary-text, rgba(0,0,0,0.75))' : textMuted }}
                    >
                      {p.weekday}
                    </div>
                    <div
                      class="mw-tl-date-num"
                      style={{ color: isSel ? 'var(--mw-primary-text, #0a0a0a)' : textMain }}
                    >
                      {p.dayNum}
                    </div>
                  </div>
                  <div class="mw-tl-date-right">
                    <div style={{ color: isSel ? 'var(--mw-primary-text, #111)' : textMain, fontWeight: 800, fontSize: '1.2rem' }}>{p.month}</div>
                    <div style={{ color: isSel ? 'var(--mw-primary-text, rgba(0,0,0,0.55))' : textMuted, fontSize: 10, letterSpacing: '0.15em' }}>{p.year}</div>
                  </div>
                </button>
              );
            })}
          </div>
        </div>
        <div class="mw-tl-date-foot">
          <button
            type="button"
            class="mw-tl-date-confirm"
            onClick={() => {
              const chosen = occurrences.find((x) => x.iso === pending) ?? occurrences[0];
              if (chosen) onConfirm(chosen);
              onClose();
            }}
            style={{
              background: 'var(--mw-primary, #ec13a4)',
              color: 'var(--mw-primary-text, #000)',
              boxShadow: '0 8px 24px color-mix(in srgb, var(--mw-primary, #ec13a4) 30%, transparent)',
            }}
          >
            Confirm
          </button>
        </div>
      </div>
    </div>
  );
};
